import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useSubmitReport } from '@/hooks/useSubmitReport'
import { cancelButtonLabel } from '@/lib/commonStrings'
import { classifyError } from '@/lib/errors'
import { toastError } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ReportContentType, ReportReason } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  contentType: ReportContentType
  contentId?: string
  reportedUserId: string
}

export function ReportDialog({ open, onOpenChange, contentType, contentId, reportedUserId }: Props) {
  const { user } = useAuth()
  const submitReport = useSubmitReport(user?.uid ?? null)
  const [reason, setReason] = useState<ReportReason>('offensive')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    if (!user) return
    setSubmitting(true)
    try {
      await submitReport({ reportedUserId, contentType, contentId, reason, message })
      toast.success(
        fbt('Report submitted. We will review it within 24 hours.', 'Toast after successfully submitting a content report'),
      )
      onOpenChange(false)
      setMessage('')
      setReason('offensive')
    } catch (err) {
      toastError(
        fbt("Couldn't submit report. Please try again.", 'Toast error when report submission fails'),
        classifyError(err),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {fbt('Report content', 'Title of the report content dialog')}
          </DialogTitle>
          <DialogDescription>
            {fbt(
              "Let us know what's wrong. We review all reports within 24 hours.",
              'Description in the report dialog explaining the review timeline',
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={v => setReason(v as ReportReason)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="offensive">
                {fbt('Offensive content', 'Report reason: offensive content')}
              </SelectItem>
              <SelectItem value="harassment">
                {fbt('Harassment', 'Report reason: harassment')}
              </SelectItem>
              <SelectItem value="spam">
                {fbt('Spam', 'Report reason: spam')}
              </SelectItem>
              <SelectItem value="other">
                {fbt('Other', 'Report reason: other')}
              </SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={fbt(
              'Any additional details (optional)',
              'Placeholder text in the report message textarea',
            )}
            rows={3}
            maxLength={500}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cancelButtonLabel()}
          </Button>
          <Button onClick={submit} disabled={submitting} variant="destructive">
            {submitting
              ? fbt('Submitting…', 'Button label while report is being submitted')
              : fbt('Report', 'Button label to submit the report')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
